import { notification } from 'antd';
import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router';

import { connectionsActions } from '@store/connections/connections.slice';
import { integrationsActions } from '@store/integrations/integrations.slice';

import { ConnectorServiceEnum } from '@enum/connector-service.enum';
import { RouteEnum } from '@enum/route.enum';
import { UnifiedApiEnum } from '@enum/unified-api.enum';
import { useCurrentApplication } from '@hook/application/use-current-application.hook';
import { useLazyQuery } from '@hook/common/use-lazy-query.hook';
import { useLoading } from '@hook/common/use-loading.hook';
import { useLazyLoadConnectionsQuery } from '@hook/connection/connection.hooks';
import { useLazyLoadIntegrationsQuery } from '@hook/intergration/integration.hooks';
import { useStoreSelector } from '@hook/store/store.hooks';
import { ConnectionStateEnum } from '@interface/connection/connection.interface';
import { isString } from '@util/data/is-exist';

const availableConnectors = new Set(Object.values(ConnectorServiceEnum));

export const useConsumerIntegrations = () => {
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  const { serviceId: paramServiceId, consumerId } = useParams();
  const [currentTab, setTab] = useState<ConnectionStateEnum>(ConnectionStateEnum.Added);
  const { current } = useCurrentApplication();
  const [currentInput, onInput] = useState<string>('');
  const [currentSector, onSector] = useState<UnifiedApiEnum | null>(null);
  const [currentFilter, onFilter] = useState<'asc' | 'desc' | null>(null);

  const {
    meta: { accessToken },
    isLoading: isConsumerLoading,
  } = useStoreSelector((state) => state.consumers);
  const { data: connections, isLoading: isLoadingConnections } = useStoreSelector((state) => state.connections);

  const { isLoading: isLoadingIntegrations } = useStoreSelector((state) => state.integrations);

  const { action: actionConnection } = useLazyQuery(
    useLazyLoadConnectionsQuery(),
    connectionsActions,
    connectionsActions.setData,
  );
  const { action: actionIntegration } = useLazyQuery(
    useLazyLoadIntegrationsQuery(),
    integrationsActions,
    integrationsActions.setData,
  );
  const isLoading = useLoading(isConsumerLoading || isLoadingConnections || isLoadingIntegrations);

  let dataConnections = Object.values(connections).filter(({ serviceId }: any) => availableConnectors.has(serviceId));

  useEffect(() => void (isString(accessToken) && actionConnection(undefined)), [accessToken]);
  useEffect(() => void (isString(current.id) && actionIntegration(current.id)), [current.id]);

  useEffect(() => {
    if (isString(paramServiceId)) {
      api.success({
        message: 'That’s great, it worked!',
        description: 'Your integration has been successfully connected and ready to use!',
      });
      navigate(
        generatePath(RouteEnum.ConsumerView.path, {
          consumerId,
        }),
        { replace: true },
      );
    }
  }, [paramServiceId]);

  if (isString(currentInput)) {
    dataConnections = dataConnections.filter(
      (connection: any) =>
        connection.name.toLowerCase().includes(currentInput.toLowerCase()) ||
        connection.serviceId.toLowerCase().includes(currentInput.toLowerCase()) ||
        connection.unifiedApi.toLowerCase().includes(currentInput.toLowerCase()),
    );
  }

  if (isString(currentSector)) {
    dataConnections = dataConnections.filter((integration: any) => integration.unifiedApi === currentSector);
  }

  if (isString(currentFilter)) {
    dataConnections = dataConnections.sort((a: any, b: any) =>
      (currentFilter === 'asc' ? a : b).name.localeCompare((currentFilter === 'asc' ? b : a).name),
    );
  }

  return {
    currentState: currentTab,
    setTab,
    data: dataConnections,
    isLoading,
    contextHolder,
    onInput,
    onSector,
    onFilter,
  };
};
